import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useTransition,
} from 'react';
import type { Tagged } from 'type-fest';
import { I18nProvider as AriaI18Provider } from 'react-aria';
import { type IntlConfig, IntlProvider } from 'react-intl';
import { LOCALE_CODES, type LocaleCode } from '@repo/shared-config/constants/locales';

const defaultRichTextElements = {
  b: (text: React.ReactNode[]) => <b>{text}</b>,
  strong: (text: React.ReactNode[]) => <strong>{text}</strong>,
  i: (text: React.ReactNode[]) => <i>{text}</i>,
  em: (text: React.ReactNode[]) => <em>{text}</em>,
  u: (text: React.ReactNode[]) => <u>{text}</u>,
  mark: (text: React.ReactNode[]) => <mark>{text}</mark>,
  br: () => <br />,
};

function noop(): void {
  // noop
}

const I18nContext = createContext<I18nContextValue | null>(null);

export interface I18nContextValue {
  locale: LocaleCode;
  setLocale: (locale: LocaleCode) => void;
  isPending: boolean;
  shouldUpdateUser: boolean;
}

type Messages = NonNullable<IntlConfig['messages']>;

export type MessagesLoader = Tagged<(locale: LocaleCode) => Resource<Messages>, 'MessagesLoader'>;

export function I18nProvider({
  defaultLocale,
  loader,
  children,
  onLocaleChange,
  shouldUpdateUser = false,
}: {
  defaultLocale: LocaleCode;
  loader: MessagesLoader;
  children: React.ReactNode;
  onLocaleChange?: (locale: LocaleCode) => void;
  /**
   * **NOTE**: This property aims at being temporary until we finish the React migration. Please don't use it.
   * When true, consumers (e.g. LanguageSelector) own backend persistence of the
   * user's locale via React Query. When false, persistence is handled outside
   * (e.g. qonto-js's Ember-Data-based save() in CustomProviders.handleLocaleChange).
   */
  shouldUpdateUser?: boolean;
}) {
  const lastKnownDefaultLocale = useRef<LocaleCode>(defaultLocale);
  const [localeState, setLocaleState] = useState<LocaleCode>(defaultLocale);
  const [isPending, startTransition] = useTransition();

  // Sync with external locale changes (e.g., from Ember's locale-manager)
  // This ensures all React roots update when locale changes in one of them
  useEffect(() => {
    if (defaultLocale !== localeState && defaultLocale !== lastKnownDefaultLocale.current) {
      startTransition(() => {
        setLocaleState(defaultLocale);
        lastKnownDefaultLocale.current = defaultLocale;
      });
    }
  }, [defaultLocale, localeState, startTransition]);

  const messages = loader(localeState).read();

  const setLocale = useCallback(
    (next: LocaleCode) => {
      onLocaleChange?.(next);
      startTransition(() => {
        setLocaleState(next);
      });
    },
    [startTransition, onLocaleChange]
  );

  const contextValue = useMemo(
    () => ({ locale: localeState, setLocale, isPending, shouldUpdateUser }),
    [localeState, setLocale, isPending, shouldUpdateUser]
  );

  const reactAriaLocale = useMemo(() => {
    // oxlint-disable-next-line typescript/switch-exhaustiveness-check
    switch (localeState) {
      case 'en':
        // Since we only pass a 2-char locale react aria default to en-US
        // making the date format mm/dd/yyyy
        return 'en-GB';
      default:
        return localeState;
    }
  }, [localeState]);

  return (
    <I18nContext.Provider value={contextValue}>
      <AriaI18Provider locale={reactAriaLocale}>
        <IntlProvider
          defaultLocale="en"
          defaultRichTextElements={defaultRichTextElements}
          locale={localeState}
          messages={messages}
          onWarn={noop}
          wrapRichTextChunksInFragment
        >
          {children}
        </IntlProvider>
      </AriaI18Provider>
    </I18nContext.Provider>
  );
}

export function useI18n() {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error('useI18n must be used within an I18nProvider');
  }
  return context;
}

interface Resource<T> {
  read: () => T;
}

function createResource<T>(promise: Promise<T>): Resource<T> {
  let status: 'pending' | 'success' | 'error' = 'pending';
  let result: T;
  let resourceError: unknown;

  // oxlint-disable-next-line promise/catch-or-return
  promise.then(
    data => {
      status = 'success';
      result = data;
    },
    error => {
      status = 'error';
      resourceError = error;
    }
  );

  return {
    read() {
      if (status === 'pending') throw promise;
      if (status === 'error') throw resourceError;
      return result;
    },
  };
}

export function createMessagesLoader(
  loader: (locale: LocaleCode) => Promise<Messages>
): MessagesLoader {
  const promiseCache = new Map<LocaleCode, Resource<Messages>>();

  // oxlint-disable-next-line typescript/consistent-type-assertions
  return ((locale: LocaleCode) => {
    if (!promiseCache.has(locale)) {
      promiseCache.set(locale, createResource(loader(locale)));
    }

    // oxlint-disable-next-line typescript/no-non-null-assertion -- the map access is guaranteed to be defined by the has check above
    return promiseCache.get(locale)!;
  }) as MessagesLoader;
}

export function isLocaleCode(value: string): value is LocaleCode {
  return LOCALE_CODES.some(code => code === value);
}
